import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
// Temporarily disabled due to ESM module issues
// import { ParallaxProvider } from 'react-scroll-parallax';
import App from './App.js';
import './index.css';

import performanceMonitor from './utils/performanceMonitor';

// Disable console logs in production builds for security and clean console
// IMPORTANT: This must run BEFORE Firebase initializes to catch Firebase's internal logging
// Also filter Firebase warnings in both development and production

// Store original console methods (needed for filtering)
const originalWarn = console.warn;
const originalError = console.error;

// Helper function to check if message should be suppressed
const shouldSuppressFirebaseWarning = (message) => {
  return (
    message.includes('Failed to obtain exclusive access to the persistence layer') ||
    message.includes('Falling back to memory cache') ||
    message.includes('multi-tab synchronization') ||
    message.includes('experimentalForceOwningTab') ||
    message.includes('persistence layer') ||
    message.includes('Error using user provided cache') ||
    message.includes('@firebase/firestore') ||
    message.includes('Firestore (9.23.0)') ||
    message.includes('Firestore (9.')
  );
};

// Override console.warn to filter Firebase persistence warnings (both dev and prod)
console.warn = (...args) => {
  const message = args.join(' ');

  // Suppress Firebase persistence warnings in all environments
  if (shouldSuppressFirebaseWarning(message)) {
    return; // Silently ignore
  }

  // In production, suppress all warnings
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  // In development, show other warnings
  originalWarn.apply(console, args);
};

// Override console.error to filter Firebase warnings and sensitive data
console.error = (...args) => {
  const message = args.join(' ');

  // Suppress Firebase persistence warnings in all environments
  if (shouldSuppressFirebaseWarning(message)) {
    return; // Silently ignore
  }

  // Sanitize sensitive data
  const sanitized = args.map(arg => {
    if (typeof arg === 'string' && (arg.includes('base64') || arg.includes('data:image'))) {
      return '[Image data hidden]';
    }
    if (typeof arg === 'object' && arg !== null) {
      const { imageUrl, images, base64Data, img, ...rest } = arg;
      return rest;
    }
    return arg;
  });

  originalError.apply(console, sanitized);
};

// Disable console methods in production only
if (process.env.NODE_ENV === 'production') {
  console.log = () => { };
  console.info = () => { };
  console.debug = () => { };
  console.trace = () => { };
  console.table = () => { };
  console.group = () => { };
  console.groupEnd = () => { };
  console.groupCollapsed = () => { };
  console.time = () => { };
  console.timeEnd = () => { };
  console.count = () => { };
  console.countReset = () => { };
  console.clear = () => { };
}

// Global error handler to prevent popup alerts
window.addEventListener('error', (event) => {
  console.error('Global error caught:', event.error);
  event.preventDefault(); // Prevent default error handling (which might show popups)
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  event.preventDefault(); // Prevent default error handling (which might show popups)
});

// Override alert, confirm, and prompt to prevent popup alerts
window.alert = (message) => { };

window.confirm = (message) => {
  return true; // Default to true for confirmations
};

window.prompt = (message, defaultValue) => {
  return defaultValue || ''; // Return default value for prompts
};

// Initialize performance monitoring
performanceMonitor.init();
window.performanceMonitor = performanceMonitor; // Make globally available

// Report metrics after page load
window.addEventListener('load', () => {
  setTimeout(() => {
    performanceMonitor.reportMetrics();
  }, 2000);
});

const root = ReactDOM.createRoot(document.getElementById('root'));

// Remove splash screen once React starts rendering
const removeSplash = () => {
  const splash = document.getElementById('vs-splash');
  if (splash) {
    splash.remove();
  }
};

root.render(
  <React.StrictMode>
    <HelmetProvider>
      {/* Temporarily disabled ParallaxProvider due to ESM module issues */}
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <App onMount={removeSplash} />
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);

// Fallback: remove splash if React takes too long

